import React from 'react';
import { Link } from 'react-router-dom';
import { FaInstagram, FaFacebookF, FaWhatsapp, FaViber, FaMapMarkerAlt, FaPhone, FaEnvelope } from 'react-icons/fa';
import { content } from '@/content/sr';
import { CONTACT_FORM_PATH } from '@/constants/paths';
import { externalUrls, safeExternalAnchor } from '@/config/envLinks';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { path: '/', label: content.navigation.home },
    { path: '/o-nama', label: content.navigation.about },
    { path: '/proizvodi', label: content.navigation.products },
    { path: '/projekti', label: content.navigation.projects },
    { path: CONTACT_FORM_PATH, label: content.navigation.collaborate },
  ];
  const scrollToTop = () => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

  const socialLinks = [
    { icon: FaInstagram, url: externalUrls.instagram, label: 'Instagram' },
    { icon: FaFacebookF, url: externalUrls.facebook, label: 'Facebook' },
    { icon: FaWhatsapp, url: externalUrls.whatsapp, label: 'WhatsApp' },
    { icon: FaViber, url: externalUrls.viber, label: 'Viber' },
  ];

  return (
    <footer className="bg-brand-primary text-white" data-testid="footer">
      <div className="container-custom py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <Link to="/" onClick={scrollToTop} className="inline-block mb-6">
              <span className="font-heading text-3xl font-semibold">
                Wall <span className="text-brand-accent">Lux</span> Trade
              </span>
            </Link>
            <p className="text-neutral-200 text-sm leading-relaxed mb-6">
              {content.footer.description}
            </p>
            {/* Social Links */}
            <div className="flex gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  {...safeExternalAnchor(social.url)}
                  className={`w-10 h-10 rounded-sm bg-white/10 flex items-center justify-center 
                           hover:bg-brand-accent transition-colors duration-300
                           ${social.url ? '' : 'opacity-40 cursor-not-allowed'}`}
                  aria-label={social.label}
                  data-testid={`social-link-${social.label.toLowerCase()}`}
                >
                  <social.icon size={18} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6">{content.footer.quickLinks}</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    onClick={() => {
                      if (!link.path.includes('#')) scrollToTop();
                    }}
                    className="text-neutral-200 hover:text-brand-accent transition-colors text-sm"
                    data-testid={`footer-link-${link.path.replace(/[#/]/g, '') || 'home'}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6">{content.footer.contact}</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <FaMapMarkerAlt className="text-brand-accent mt-1 flex-shrink-0" />
                <span className="text-neutral-200 text-sm">{content.company.address}</span>
              </li>
              <li className="flex items-center gap-3">
                <FaPhone className="text-brand-accent flex-shrink-0" />
                <a 
                  href={`tel:${content.company.phone.replace(/\s/g, '')}`}
                  className="text-neutral-200 hover:text-brand-accent transition-colors text-sm"
                  data-testid="footer-phone"
                >
                  {content.company.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <FaEnvelope className="text-brand-accent flex-shrink-0" />
                <a 
                  href={`mailto:${content.company.email}`}
                  className="text-neutral-200 hover:text-brand-accent transition-colors text-sm"
                  data-testid="footer-email"
                >
                  {content.company.email}
                </a>
              </li>
            </ul>
          </div>

          {/* Working Hours */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6">{content.contact.info.workingHoursTitle}</h4>
            <p className="text-neutral-200 text-sm leading-relaxed">
              {content.company.workingHours}
            </p>
            <div className="mt-6">
              <Link 
                to={CONTACT_FORM_PATH} 
                className="btn-accent inline-block text-xs"
                data-testid="footer-cta"
              >
                {content.contact.cta.call}
              </Link>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-white/10 mt-12 pt-8">
          <p className="text-center text-neutral-200 text-sm">
            © {currentYear} Powered by Limtech Solutions
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
